import React from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Steps } from "./step-library"
import { Container, FlexContainer, H2 } from "@carrotfertility/carotene"
import useButtonSingleSelect from "../../hooks/useButtonSingleSelect"
import ButtonSelectGroup from "../../shared/ButtonSelectGroup"
import { Body } from "components/views/atoms/Bodies"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"
import { useRoutingInfo } from "../../context/RoutingInfoContext"
import { useCurrentUser } from "../../../context/user/UserContext"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"

const ViewName = Steps.MOST_HELPFUL

const mostHelpfulOptions = [
  { title: "Discuss my options and journey", id: "DISCUSS_OPTIONS" },
  { title: "Chat about menstrual cycles and evaluating fertility", id: "CHAT_FERTILITY" },
  { title: "Chat about nutrition and lifestyle", id: "CHAT_LIFESTYLE" },
  { title: "Chat about health-related questions", id: "CHAT_HEALTH" },
  { title: "Help with pathways or regulations", id: "HELP_PATHWAY" },
  { title: "Help finding or choosing a provider", id: "HELP_FINDING_PROVIDER" },
  { title: "LGBTQ+ family-forming guidance", id: "LGBTQ_FAMILY" },
  { title: "Financial information or reimbursement", id: "FINANCE_INFO" },
  { title: "Advice on making the most of my benefit", id: "BENEFIT_ADVICE" },
  { title: "I’m not sure", id: "NOT_SURE" }
]

function getHelpOptions(journeyType) {
  switch (journeyType) {
    case "ADOPTION":
    case "GESTATIONAL":
      return [
        "DISCUSS_OPTIONS",
        "HELP_PATHWAY",
        "HELP_FINDING_PROVIDER",
        "LGBTQ_FAMILY",
        "FINANCE_INFO",
        "BENEFIT_ADVICE",
        "NOT_SURE"
      ]
    case "PRESERVATION":
    case "ASSISTED_REPRODUCTION":
      return [
        "DISCUSS_OPTIONS",
        "CHAT_HEALTH",
        "LGBTQ_FAMILY",
        "FINANCE_INFO",
        "BENEFIT_ADVICE",
        "HELP_FINDING_PROVIDER",
        "HELP_PATHWAY",
        "NOT_SURE"
      ]
    case "TRY_PREGNANT":
      return [
        "DISCUSS_OPTIONS",
        "CHAT_FERTILITY",
        "CHAT_LIFESTYLE",
        "LGBTQ_FAMILY",
        "FINANCE_INFO",
        "BENEFIT_ADVICE",
        "HELP_FINDING_PROVIDER",
        "HELP_PATHWAY",
        "NOT_SURE"
      ]
    case "PREGNANT":
      return ["CHAT_LIFESTYLE", "CHAT_HEALTH", "LGBTQ_FAMILY", "BENEFIT_ADVICE", "NOT_SURE"]
    case "EXPLORING":
      return ["CHAT_HEALTH", "LGBTQ_FAMILY", "FINANCE_INFO", "BENEFIT_ADVICE", "HELP_FINDING_PROVIDER", "NOT_SURE"]
    default:
      return []
  }
}

function filterOptions(helpOptions, lgbtqEnabled, carrotLite) {
  let filteredOptions = helpOptions
  if (!lgbtqEnabled) {
    filteredOptions = filteredOptions.filter((opt) => opt !== "LGBTQ_FAMILY")
  }
  if (carrotLite) {
    filteredOptions = filteredOptions.filter((opt) => opt !== "FINANCE_INFO")
  }
  return filteredOptions
}

function useOptionsForJourneyType() {
  const { routingData } = useRoutingInfo()
  const memberJourney = routingData?.journey
  const lgbtqEnabled = useSelector(getBenefitConfiguration).featureConfig.isLgtbqEnabled
  const { company } = useCurrentUser()

  const journeyHelpOptions = filterOptions(getHelpOptions(memberJourney), lgbtqEnabled, company?.carrotLite)
  const buttonOptions = mostHelpfulOptions.filter((option) => journeyHelpOptions.includes(option.id))
  return {
    buttonOptions
  }
}

export default function MostHelpfulStep(): JSX.Element {
  const { routingData, updateRoutingInfo } = useRoutingInfo()
  const defaultValue = routingData?.mostHelpfulNow
  const { setNextStep } = useStateMachine(ViewName, defaultValue ? "" : null)
  const { buttonOptions } = useOptionsForJourneyType()
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  const { valid, ...buttonProps } = useButtonSingleSelect({
    options: buttonOptions,
    defaultValue,
    onSubmit: onClickContinue
  })
  async function onClickContinue(selectedValue: string): Promise<void> {
    if (selectedValue !== null) {
      await handleContinueLoading(() => updateRoutingInfo({ mostHelpfulNow: selectedValue }))
      setNextStep(ViewName, "")
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onClickContinue(defaultValue)
      }}
    >
      <Container stack="large">
        <H2 stack="tiny">What would be most helpful right now?</H2>
        <Body>Choose the option that feels like the top priority, just to get things started.</Body>
      </Container>
      <Container>
        <ButtonSelectGroup {...buttonProps} />
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="flex-end">
        <ArrowSubmitButton enabled={valid && !continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}
